/**
 * Axis-aligned bounding box of a rotated rectangle, in millimetres.
 *
 * Every collision, obstacle and page-containment check in the layout pipeline
 * (`validateLayoutDocumentGeometry`, `solveLayout`, `updateLayoutAssetGeometry`,
 * `assertVisibleAssetCollisions`, `assertGeometryInsideCanvas`,
 * `assertNoVisibleCollisions`, `assertLayoutGeometry`) goes through
 * `collisionRectMm` so a rotated asset collides like what the operator sees.
 * The rotation itself stays a render concern: `geometry` is the unrotated
 * local box and the rotation happens around its center.
 */

export interface RotatedRectMm {
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
}

const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Identical to `geometry` at 0/180° (and any multiple of 360°), swaps width
 * and height around the center at ±90°, and returns the conservative AABB
 * `w·|cosθ| + h·|sinθ| × w·|sinθ| + h·|cosθ|` at every other angle — the
 * exact axis-aligned extent of the rotated box, centered where the box is.
 */
export function collisionRectMm(geometry: RotatedRectMm, rotationDeg: number): RotatedRectMm {
  const normalizedDeg = ((rotationDeg % 360) + 360) % 360;
  if (normalizedDeg % 180 === 0) {
    return { ...geometry };
  }
  const centerX = geometry.xMm + geometry.widthMm / 2;
  const centerY = geometry.yMm + geometry.heightMm / 2;
  if (normalizedDeg % 90 === 0) {
    return {
      xMm: centerX - geometry.heightMm / 2,
      yMm: centerY - geometry.widthMm / 2,
      widthMm: geometry.heightMm,
      heightMm: geometry.widthMm,
    };
  }
  const radians = degreesToRadians(rotationDeg);
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const widthMm = geometry.widthMm * cos + geometry.heightMm * sin;
  const heightMm = geometry.widthMm * sin + geometry.heightMm * cos;
  return {
    xMm: centerX - widthMm / 2,
    yMm: centerY - heightMm / 2,
    widthMm,
    heightMm,
  };
}
